import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, EmptyState, Text } from 'onefaceui-react-components';
import { useDispatch, useSelector } from 'react-redux';
import { actionCreators as actionsModals } from 'react/api/actions/modals';
import { actionCreators as actionsPhotoAlbum } from 'react/api/actions/photoAlbum';

import { formatDate, getResourceTo } from 'react/utils/utils';
import {
  AlbumItemCard,
  AlbumItemCardBody,
  AlbumItemCardFooter,
  AlbumItemCardHeader,
  AlbumItemCardImage
} from './PhotoAlbumItemStyles';
import { PHOTO_ALBUM_MODAL_KEY } from 'react/containers/modals/PhotoAlbumModal/PhotoAlbumModal';

export function PhotoAlbumItem({ id, description, photos, time }) {
  const dispatch = useDispatch();
  const { selectedAlbums } = useSelector((state) => state.photoAlbum).toJS();

  const { photoThumbnail, hasPhotoAndDescription, hasPhotos } = useMemo(() => {
    return {
      photoThumbnail: photos[0]?.base64Thumbnail,
      hasPhotos: photos.length > 0,
      hasPhotoAndDescription: photos.length > 0 && description
    };
  }, [photos]);

  const handleClickAlbum = useCallback(() => {
    dispatch(
      actionsModals.openModal({
        modalKey: PHOTO_ALBUM_MODAL_KEY,
        data: { albumId: id }
      })
    );
  }, [photos]);

  const handleCheckAlbum = useCallback(() => {
    const albumIds = Object.keys(selectedAlbums).map(Number);

    if (!albumIds.includes(id)) {
      dispatch(actionsPhotoAlbum.selectAlbum(id));
      return;
    }

    dispatch(actionsPhotoAlbum.deselectAlbum(id));
  }, [selectedAlbums, photos]);

  const isChecked = Object.entries(selectedAlbums).some(
    ([albumId, value]) => Number(albumId) === id && !!value
  );

  return (
    <AlbumItemCard
      href="#/PhotoAlbum"
      onClick={(e) => {
        e.preventDefault();
        handleClickAlbum();
      }}
      className={hasPhotos ? 'hasPhoto' : ''}
      data-testid="photoalbum-item"
    >
      <AlbumItemCardHeader data-testid="photoalbum-item-header">
        <Checkbox
          dataTestId={`checkbox-item-${id}`}
          id={id}
          checked={isChecked}
          onChange={handleCheckAlbum}
          className="oneface-checkbox"
          tabIndex="0"
          disabled={!hasPhotos}
        />
        <Text size="xs">
          {hasPhotoAndDescription
            ? description
            : getResourceTo('PhotoAlbum.untitled.album')}
        </Text>
      </AlbumItemCardHeader>
      <AlbumItemCardBody>
        <AlbumItemCardImage
          data-testid="photoalbum-container-thumbnail"
          id={`photoAlbum_${id}`}
        >
          {photoThumbnail ? (
            <img
              src={`data:image/jpeg;charset=utf-8;base64,${photoThumbnail}`}
              alt={`Thumbnail ${description}`}
            />
          ) : (
            <EmptyState type="photos" message="" />
          )}
        </AlbumItemCardImage>
      </AlbumItemCardBody>
      <AlbumItemCardFooter data-testid="photoalbum-item-footer">
        {formatDate(time)}
      </AlbumItemCardFooter>
    </AlbumItemCard>
  );
}

PhotoAlbumItem.propTypes = {
  id: PropTypes.number.isRequired,
  description: PropTypes.string,
  time: PropTypes.string.isRequired,
  photos: PropTypes.arrayOf(PropTypes.object)
};

PhotoAlbumItem.defaultProps = {
  photos: []
};
